import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { logger } from '../utils/logger';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Create a stable query client instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time - how long data is considered fresh (5 minutes)
      staleTime: 5 * 60 * 1000,
      
      // Cache time - how long inactive data stays in cache (30 minutes)
      cacheTime: 30 * 60 * 1000,
      
      // Retry configuration
      retry: (failureCount: number, error: any) => {
        // Don't retry on 4xx errors except 408 (timeout)
        if (error?.status >= 400 && error?.status < 500 && error?.status !== 408) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch configuration
      refetchOnWindowFocus: false, // Don't refetch on window focus (enterprise apps often stay open)
      refetchOnReconnect: true,    // Refetch when connection is restored
      refetchOnMount: true,        // Refetch when component mounts
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
      
      // Retry delay for mutations
      retryDelay: 2000,
    },
  },
});

interface QueryProviderProps {
  children: React.ReactNode;
}

export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* Show React Query DevTools in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools
          initialIsOpen={false}
          position="bottom-right"
        />
      )}
    </QueryClientProvider>
  );
};

// Export query client for direct usage if needed
export { queryClient };

// Utility function to invalidate queries
export const invalidateQueries = (queryKeys: string[][]) => {
  queryKeys.forEach(queryKey => {
    queryClient.invalidateQueries(queryKey);
  });
};

// Utility function to prefetch data
export const prefetchQuery = async (queryKey: string[], queryFn: () => Promise<any>) => {
  await queryClient.prefetchQuery(queryKey, queryFn);
};

// Query client error handler
queryClient.getQueryCache().subscribe((event: any) => {
  if (event?.type === 'observerResultsUpdated') {
    const { query } = event;
    if (query.state.error) {
      logger.error('Query Error:', {
        queryKey: query.queryKey,
        error: query.state.error,
        timestamp: new Date().toISOString(),
      });
      
      // Handle specific error types
      const error = query.state.error as any;
      
      // Handle authentication errors
      if (error?.status === 401) {
        // Redirect to login or refresh token
        logger.warn('Authentication error detected, user may need to re-login');
      }
      
      // Handle server errors
      if (error?.status >= 500) {
        logger.error('Server error detected:', error.message);
      }
    }
  }
});

export default QueryProvider;